import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useNavigate, useLocation, matchRoutes } from 'react-router-dom';
import {getToken, setToken, logout as logoutUser} from "../utils/auth";

interface AuthContextType {
  token: string | null;
  setToken: (token: string | null) => void;
  logout: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode; // Explicitly define the type for children
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setTokenState] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    console.log("URL Parameters:", location.search); // Debugging line
    const tokenFromUrl = urlParams.get('token');
    if (tokenFromUrl) {
      console.log("Token from URL:", tokenFromUrl); // Debugging line
      setTokenAndState(tokenFromUrl);
      navigate('/dashboard'); // Redirect after storing the token
    } else {
      const tokenFromStorage = getToken();
      if (tokenFromStorage) {
        setTokenState(tokenFromStorage);
        if(!!matchRoutes([{ path: '/login' }], location)) {
          navigate('/dashboard');
        }
      }
    }
  }, [location, location.search, navigate]);

  const setTokenAndState = (token: string | null) => {
    setToken(token)
    setTokenState(token);
  };

  const logout = async () => {
    await logoutUser();
    setTokenState(null);
    navigate('/login');
  };

  return (
    <AuthContext.Provider value={{ token, setToken: setTokenAndState, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthProvider, useAuth };
